import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, RefreshCw, Router, Server, Copy, Check, Trash2, ArrowDown, ArrowUp } from "lucide-react";

function fmtBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function fmtHandshake(ts) {
  if (!ts) return "jamais";
  const secs = Math.floor(Date.now() / 1000) - ts;
  if (secs < 60) return `il y a ${secs}s`;
  if (secs < 3600) return `il y a ${Math.floor(secs / 60)}min`;
  if (secs < 86400) return `il y a ${Math.floor(secs / 3600)}h`;
  return `il y a ${Math.floor(secs / 86400)}j`;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1 rounded-sm bg-zinc-800 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-700"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copié" : "Copier"}
    </button>
  );
}

function ModeCard({ icon: Icon, title, description, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer rounded-sm border p-4 transition-all ${
        selected
          ? "border-zinc-900 bg-zinc-50"
          : "border-zinc-200 bg-white hover:border-zinc-400"
      }`}
    >
      {selected && (
        <span className="absolute right-3 top-3 text-xs font-bold text-zinc-900">✓</span>
      )}
      <Icon className="mb-2 h-5 w-5 text-zinc-600" />
      <div className="text-sm font-semibold text-zinc-900">{title}</div>
      <div className="mt-1 text-xs leading-relaxed text-zinc-500">{description}</div>
    </div>
  );
}

function UnconfiguredView({ onModeSelect }) {
  const [selected, setSelected] = useState("wg_vpn");

  return (
    <div className="mt-4 space-y-4">
      <div>
        <h3 className="font-head text-base font-bold text-zinc-900">Mode de provisioning TFTP</h3>
        <p className="mt-1 text-sm text-zinc-500">
          Optionnel — les téléphones fonctionnent sans TFTP si déjà configurés manuellement.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <ModeCard
          icon={Shield}
          title="WireGuard VPN"
          description="TFTP + SIP via tunnel chiffré. Recommandé si MikroTik RouterOS 7+."
          selected={selected === "wg_vpn"}
          onClick={() => setSelected("wg_vpn")}
        />
        <ModeCard
          icon={Router}
          title="RouterOS 7 Script"
          description="MikroTik pull les configs via HTTPS et sert le TFTP en local."
          selected={selected === "routeros7"}
          onClick={() => setSelected("routeros7")}
        />
        <ModeCard
          icon={Server}
          title="VM / LXC Relay"
          description="Debian LXC on-site. Compatible tout routeur."
          selected={selected === "vm_relay"}
          onClick={() => setSelected("vm_relay")}
        />
      </div>
      <Button
        onClick={() => onModeSelect(selected)}
        className="rounded-sm bg-zinc-900 hover:bg-zinc-800"
      >
        Configurer →
      </Button>
    </div>
  );
}

function WgSetupWizard({ hotelId, onComplete, onCancel }) {
  const [step, setStep] = useState("loading");
  const [wgData, setWgData] = useState(null);
  const [commands, setCommands] = useState("");
  const [peerPubkey, setPeerPubkey] = useState("");
  const [phoneSubnet, setPhoneSubnet] = useState("");
  const [phoneGateway, setPhoneGateway] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.post(`/hotels/${hotelId}/network/setup`);
        setWgData(r.data);
        const cmd = await api.get(`/hotels/${hotelId}/network/mikrotik-commands`);
        setCommands(cmd.data.commands);
        setStep("commands");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        onCancel();
      }
    })();
  }, [hotelId]);

  const activate = async () => {
    if (!peerPubkey.trim() || peerPubkey.trim().length < 40) {
      toast.error("Clé publique invalide (44 caractères base64 attendus)");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/hotels/${hotelId}/network/peer-key`, {
        peer_pubkey: peerPubkey.trim(),
        phone_subnet: phoneSubnet.trim() || null,
        phone_gateway: phoneGateway.trim() || null,
      });
      toast.success("Tunnel WireGuard activé");
      onComplete();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (step === "loading") {
    return <div className="mt-4 font-mono text-sm text-zinc-400">Génération du tunnel en cours…</div>;
  }

  return (
    <div className="mt-4 max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-head text-base font-bold text-zinc-900">Configuration WireGuard</h3>
        <button onClick={onCancel} className="text-xs text-zinc-400 hover:text-zinc-700">
          Annuler
        </button>
      </div>

      <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-xs text-white">✓</span>
          Tunnel généré par HotelSIP
        </div>
        {wgData && (
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-zinc-600">
            <div>
              <span className="font-medium">Clé pub serveur :</span>
              <div className="mt-1 break-all font-mono text-zinc-400">{wgData.server_pubkey}</div>
            </div>
            <div>
              <span className="font-medium">IPs assignées :</span>
              <div className="mt-1 font-mono">
                Serveur : {wgData.server_wg_ip}<br />
                MikroTik : {wgData.peer_wg_ip}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-sm border border-zinc-200 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900 text-xs text-white">2</span>
            Coller sur le MikroTik (terminal Winbox)
          </div>
          <CopyButton text={commands} />
        </div>
        <pre className="overflow-x-auto rounded-sm bg-zinc-900 p-3 text-[11px] leading-relaxed text-zinc-200">
          {commands}
        </pre>
      </div>

      <div className="rounded-sm border border-zinc-200 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-700">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900 text-xs text-white">3</span>
          Clé publique du MikroTik
        </div>
        <p className="mb-2 text-xs text-zinc-500">
          Sur le MikroTik :{" "}
          <code className="rounded bg-zinc-100 px-1">/interface wireguard print</code>
        </p>
        <Input
          value={peerPubkey}
          onChange={(e) => setPeerPubkey(e.target.value)}
          placeholder="XoUujRLdfY5ri4TC4L/K25/AoqAe61Py+xAmGwHkP0Q="
          className="font-mono text-xs"
        />
      </div>

      <div className="rounded-sm border border-zinc-200 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-700">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-200 text-xs text-zinc-600">4</span>
          Réseau des téléphones{" "}
          <span className="font-normal text-zinc-400">(optionnel)</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Subnet CIDR</Label>
            <Input
              value={phoneSubnet}
              onChange={(e) => setPhoneSubnet(e.target.value)}
              placeholder="10.21.50.0/24"
              className="mt-1 font-mono text-xs"
            />
          </div>
          <div>
            <Label className="text-xs">Gateway</Label>
            <Input
              value={phoneGateway}
              onChange={(e) => setPhoneGateway(e.target.value)}
              placeholder="10.21.50.1"
              className="mt-1 font-mono text-xs"
            />
          </div>
        </div>
      </div>

      <Button
        onClick={activate}
        disabled={saving || !peerPubkey.trim()}
        className="rounded-sm bg-zinc-900 hover:bg-zinc-800"
      >
        {saving ? "Activation…" : "Activer le tunnel →"}
      </Button>
    </div>
  );
}

function WgStatusPanel({ hotelId, onDelete }) {
  const [status, setStatus] = useState(null);
  const [dhcpCmd, setDhcpCmd] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get(`/hotels/${hotelId}/network/status`);
      setStatus(r.data);
      if (r.data.server_wg_ip) {
        const parts = r.data.server_wg_ip.split(".").map(Number);
        const hex = "0x" + parts.map((p) => p.toString(16).padStart(2, "0").toUpperCase()).join("");
        setDhcpCmd(`/ip dhcp-server option add name=tftp-server code=150 value="${hex}"`);
      }
    } catch (e) {
      console.error("network/status error", e);
    }
  }, [hotelId]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30_000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const handleDelete = async () => {
    if (!window.confirm("Supprimer le tunnel WireGuard ?")) return;
    setDeleting(true);
    try {
      await api.delete(`/hotels/${hotelId}/network`);
      toast.success("Tunnel supprimé");
      onDelete();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  if (!status) {
    return <div className="mt-4 font-mono text-sm text-zinc-400">Chargement du statut…</div>;
  }

  return (
    <div className="mt-4 max-w-2xl space-y-4">
      {/* Bannière statut */}
      <div
        className={`flex items-center gap-3 rounded-sm border p-3 ${
          status.connected
            ? "border-green-200 bg-green-50"
            : "border-amber-200 bg-amber-50"
        }`}
      >
        <span
          className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${
            status.connected ? "bg-green-500 animate-pulse" : "bg-amber-400"
          }`}
        />
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-semibold ${status.connected ? "text-green-800" : "text-amber-800"}`}>
            {status.connected ? "Tunnel actif" : "Tunnel inactif"}
          </span>
          <span className={`ml-2 text-xs ${status.connected ? "text-green-600" : "text-amber-600"}`}>
            — dernier handshake {fmtHandshake(status.last_handshake_ts)}
          </span>
        </div>
        <button onClick={fetchStatus} className="text-zinc-400 hover:text-zinc-700">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Trafic rx/tx */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 rounded-sm bg-zinc-50 p-3">
          <ArrowDown className="h-4 w-4 text-green-600 flex-shrink-0" />
          <div>
            <div className="text-xs text-zinc-500">Reçu</div>
            <div className="font-mono text-sm font-semibold text-zinc-900">{fmtBytes(status.transfer_rx)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-sm bg-zinc-50 p-3">
          <ArrowUp className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <div>
            <div className="text-xs text-zinc-500">Envoyé</div>
            <div className="font-mono text-sm font-semibold text-zinc-900">{fmtBytes(status.transfer_tx)}</div>
          </div>
        </div>
      </div>

      {/* Endpoint + infos réseau */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "Endpoint MikroTik", value: status.endpoint || "inconnu" },
          { label: "TFTP server (option 150)", value: status.server_wg_ip },
          { label: "Clé pub serveur", value: status.server_pubkey },
          { label: "Clé pub MikroTik", value: status.peer_pubkey },
          { label: "Réseau téléphones", value: status.phone_subnet || "Non renseigné" },
          { label: "IP tunnel serveur / pair", value: `${status.server_wg_ip} / ${status.peer_wg_ip}` },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-sm bg-zinc-50 p-3">
            <div className="text-xs text-zinc-500">{label}</div>
            <div className="mt-1 break-all font-mono text-xs font-semibold text-zinc-900">{value}</div>
          </div>
        ))}
      </div>

      {dhcpCmd && (
        <div className="flex items-center gap-3 rounded-sm border border-amber-200 bg-amber-50 p-3">
          <div className="flex-1">
            <div className="text-xs font-medium text-amber-800">DHCP option 150 — coller sur MikroTik</div>
            <code className="text-xs text-amber-700">{dhcpCmd}</code>
          </div>
          <CopyButton text={dhcpCmd} />
        </div>
      )}

      <div className="pt-2">
        <Button
          variant="outline"
          onClick={handleDelete}
          disabled={deleting}
          className="rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          {deleting ? "Suppression…" : "Supprimer le tunnel"}
        </Button>
      </div>
    </div>
  );
}

function RouterOsPanel({ hotelId, onCancel }) {
  const [downloading, setDownloading] = useState(false);

  const downloadScript = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/hotels/${hotelId}/provision-relay-script`);
      const blob = new Blob([r.data.script], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.data.filename || "hotelsip-routeros.rsc";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur lors du téléchargement du script");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-4 max-w-lg space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-head text-base font-bold text-zinc-900">RouterOS 7 — Script TFTP</h3>
        <button onClick={onCancel} className="text-xs text-zinc-400 hover:text-zinc-700">
          ← Changer de mode
        </button>
      </div>
      <div className="rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Le script se synchronise automatiquement avec HotelSIP et sert le TFTP en local sur le MikroTik.
        À uploader une seule fois, puis planifier via le scheduler RouterOS.
      </div>
      <Button
        onClick={downloadScript}
        disabled={downloading}
        className="rounded-sm bg-zinc-900 hover:bg-zinc-800"
      >
        {downloading ? "Préparation…" : "⬇ Télécharger hotelsip-routeros.rsc"}
      </Button>
    </div>
  );
}

function VmRelayPanel({ hotelId, onCancel }) {
  const [downloading, setDownloading] = useState(false);

  const downloadScript = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/hotels/${hotelId}/provision-relay-script`);
      const blob = new Blob([r.data.script], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.data.filename || "hotelsip-relay-install.sh";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur lors du téléchargement du script");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-4 max-w-lg space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-head text-base font-bold text-zinc-900">VM / LXC Relay</h3>
        <button onClick={onCancel} className="text-xs text-zinc-400 hover:text-zinc-700">
          ← Changer de mode
        </button>
      </div>
      <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
        Installe tftpd-hpa sur une VM ou LXC Debian 12 dans le LAN de l'hôtel.
        Compatible avec tout routeur (pas besoin de MikroTik).
      </div>
      <Button
        onClick={downloadScript}
        disabled={downloading}
        className="rounded-sm bg-zinc-900 hover:bg-zinc-800"
      >
        {downloading ? "Préparation…" : "⬇ Télécharger relay-install.sh"}
      </Button>
    </div>
  );
}

export default function NetworkTab({ hotelId }) {
  const [view, setView] = useState("loading");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/hotels/${hotelId}/network/status`);
        const s = r.data;
        if (s.status === "active") {
          setView("wg_active");
        } else if (s.status === "pending") {
          setView("wg_setup");
        } else {
          setView("unconfigured");
        }
      } catch {
        setView("unconfigured");
      }
    })();
  }, [hotelId]);

  if (view === "loading") {
    return <div className="mt-4 font-mono text-sm text-zinc-400">Chargement…</div>;
  }

  if (view === "unconfigured") {
    return (
      <UnconfiguredView
        onModeSelect={(mode) => {
          if (mode === "wg_vpn") setView("wg_setup");
          else if (mode === "routeros7") setView("routeros7");
          else setView("vm_relay");
        }}
      />
    );
  }

  if (view === "wg_setup") {
    return (
      <WgSetupWizard
        hotelId={hotelId}
        onComplete={() => setView("wg_active")}
        onCancel={() => setView("unconfigured")}
      />
    );
  }

  if (view === "wg_active") {
    return <WgStatusPanel hotelId={hotelId} onDelete={() => setView("unconfigured")} />;
  }

  if (view === "routeros7") {
    return <RouterOsPanel hotelId={hotelId} onCancel={() => setView("unconfigured")} />;
  }

  if (view === "vm_relay") {
    return <VmRelayPanel hotelId={hotelId} onCancel={() => setView("unconfigured")} />;
  }

  return null;
}
